import React from 'react';
import { observer } from 'mobx-react-lite';
import {
   Badge,
   Box,
   Button,
   Checkbox,
   Dialog,
   Flex,
   Heading,
   ScrollArea,
   Table,
   Text,
   TextField,
} from '@radix-ui/themes';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import { Paginator } from 'shared/components/pagination';
import { DateUtilsService } from 'shared/utils/date-utils.service';
import { useSimpleResumeManager } from './simple-resume-manager.provider';
import { SimpleResumeManagerFilters } from './simple-resume-manager-filters';
import { SimpleResumeActionsMenu } from './simple-resume-actions-menu';
import { SimpleResumeConfirmDialog } from './simple-resume-confirm-dialog';

import './simple-resume-manager.scss';
import { AdminSideBar } from 'admin/elements/side-bar';

const SKELETON_ROWS = 12;
type SimpleResumeManagerAction = 'ACTIVATE_ALL' | 'DEACTIVATE_ALL' | 'DELETE_ALL' | 'EMPTY';

const renderUpdatedDate = (date?: string) => {
   if (!date) {
      return '-';
   }

   return DateUtilsService.toAppFormat(date);
};

function SimpleResumeManagerView() {
   const simpleResumeManager = useSimpleResumeManager();
   const [searchParams, setSearchParams] = useSearchParams();
   const initFromQueryDone = React.useRef(false);
   const [open, setOpen] = React.useState<boolean>(false);
   const [action, setAction] = React.useState<SimpleResumeManagerAction>('EMPTY');

   React.useEffect(() => {
      if (initFromQueryDone.current) {
         return;
      }
      initFromQueryDone.current = true;

      const rawPage = searchParams.get('page');
      const parsedPage = Number(rawPage);
      const page = Number.isFinite(parsedPage) && parsedPage >= 1 ? parsedPage : 1;
      simpleResumeManager.loadPage(page - 1);
   }, [searchParams, simpleResumeManager]);

   React.useEffect(() => {
      const nextPage = String(simpleResumeManager.pagination.page + 1);
      if (searchParams.get('page') === nextPage) {
         return;
      }

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('page', nextPage);
      setSearchParams(nextSearchParams, { replace: true });
   }, [searchParams, setSearchParams, simpleResumeManager.pagination.page]);

   const skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, index) => index);

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="simple-resume-manager">
            <Flex width="270px" minWidth="270px">
               <AdminSideBar />
            </Flex>
            <Box className="simple-resume-manager__content">
               <Flex py="1" mx="3" justify="between" align="center" gap="3">
                  <Heading as="h1" weight="medium">
                     Simple Resume List
                  </Heading>

                  <Flex align="center" gap="2">
                     <Text size="3" color="gray">
                        Records:
                     </Text>
                     <Text size="3" weight="medium">
                        {simpleResumeManager.recordStart} - {simpleResumeManager.recordEnd} of{' '}
                        {simpleResumeManager.totalCount}
                     </Text>
                  </Flex>
               </Flex>

               <SimpleResumeManagerFilters />

               <ScrollArea
                  scrollbars="both"
                  type="always"
                  className="simple-resume-manager__scroll-area"
               >
                  <Box p="3" className="simple-resume-manager__table-wrap">
                     <Table.Root size="3" layout="auto" variant="surface">
                        <Table.Header>
                           <Table.Row>
                              <Table.ColumnHeaderCell width="80px" maxWidth="80px" align="center">
                              </Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Jobseeker ID</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Jobseeker Name</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Resume ID</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Resume Name</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Location</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>Updated</Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell justify="center">
                                 Action
                              </Table.ColumnHeaderCell>
                           </Table.Row>
                        </Table.Header>

                        <Table.Body>
                           {simpleResumeManager.loading &&
                              skeletonRows.map((row) => (
                                 <Table.Row key={`skeleton-${row}`} className="table-skeleton-row">
                                    <Table.Cell width="80px" justify="center">
                                       <div className="table-skeleton table-skeleton--checkbox" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--id" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--name" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--email" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--id" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <Flex direction="column" gap="2">
                                          <div className="table-skeleton table-skeleton--title" />
                                          <div className="table-skeleton table-skeleton--badge" />
                                       </Flex>
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--location" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--category" />
                                    </Table.Cell>
                                    <Table.Cell>
                                       <div className="table-skeleton table-skeleton--date" />
                                    </Table.Cell>
                                    <Table.Cell justify="center" className="action-cell">
                                       <Flex justify="center" gap="2">
                                          <div className="table-skeleton table-skeleton--button" />
                                          <div className="table-skeleton table-skeleton--button" />
                                       </Flex>
                                    </Table.Cell>
                                 </Table.Row>
                              ))}

                           {!simpleResumeManager.loading &&
                              simpleResumeManager.resumes.length === 0 && (
                                 <Table.Row>
                                    <Table.Cell colSpan={10} align="center">
                                       No records found
                                    </Table.Cell>
                                 </Table.Row>
                              )}

                           {!simpleResumeManager.loading &&
                              simpleResumeManager.resumes.map((resume) => {
                                 const categoryLabel = Array.isArray(resume.category)
                                    ? resume.category.join(', ')
                                    : (resume.category_name ?? '-');
                                 const updatedAt = resume.updated ?? resume.modified;

                                 return (
                                    <Table.Row key={resume.id}>
                                       <Table.Cell width="80px" justify="center">
                                          <Checkbox
                                             color="blue"
                                             size="2"
                                             highContrast
                                             checked={simpleResumeManager.idsArray.includes(
                                                resume.id
                                             )}
                                             onCheckedChange={() => {
                                                simpleResumeManager.onToggleById(resume.id);
                                             }}
                                          />
                                       </Table.Cell>
                                       <Table.Cell>{resume.jobseeker_id}</Table.Cell>
                                       <Table.Cell>{resume.jobseeker_name}</Table.Cell>
                                       <Table.Cell>{resume.jobseeker_email}</Table.Cell>
                                       <Table.Cell>{resume.id}</Table.Cell>
                                       <Table.Cell>
                                          <Flex direction="column" gap="1">
                                             <Text>{resume.position}</Text>
                                             {resume.resume_verified === 1 && (
                                                <Badge color="green" variant="soft" radius="full">
                                                   Verified
                                                </Badge>
                                             )}
                                          </Flex>
                                       </Table.Cell>
                                       <Table.Cell>{resume.city?.city_name ?? '-'}</Table.Cell>
                                       <Table.Cell>{categoryLabel}</Table.Cell>
                                       <Table.Cell>{renderUpdatedDate(updatedAt)}</Table.Cell>
                                       <Table.Cell justify="center" className="action-cell">
                                          <SimpleResumeActionsMenu resume={resume} />
                                       </Table.Cell>
                                    </Table.Row>
                                 );
                              })}
                        </Table.Body>
                     </Table.Root>
                  </Box>
               </ScrollArea>

               <Flex className="simple-resume-manager__pagination" py="2" px="3">
                  <Flex gap="4" px="3">
                     <Button
                        variant="surface"
                        size="3"
                        color="blue"
                        disabled={simpleResumeManager.disableBulkActions}
                        onClick={() => {
                           setOpen(true);
                           setAction('ACTIVATE_ALL');
                        }}
                     >
                        <Text weight="regular" size="2">
                           ACTIVATE
                        </Text>
                     </Button>

                     <Button
                        variant="surface"
                        size="3"
                        color="blue"
                        disabled={simpleResumeManager.disableBulkActions}
                        onClick={() => {
                           setOpen(true);
                           setAction('DEACTIVATE_ALL');
                        }}
                     >
                        <Text weight="regular" size="2">
                           DEACTIVATE
                        </Text>
                     </Button>

                     <Button
                        size="3"
                        disabled={simpleResumeManager.disableBulkActions}
                        color="red"
                        onClick={() => {
                           setOpen(true);
                           setAction('DELETE_ALL');
                        }}
                     >
                        <Text weight="regular" size="2">
                           DELETE
                        </Text>
                     </Button>
                  </Flex>

                  <Paginator
                     totalPages={simpleResumeManager.totalPages}
                     currentPage={simpleResumeManager.pagination.page}
                     disabled={simpleResumeManager.loading}
                     onPageClick={(page) => {
                        simpleResumeManager.loadPage(page);
                     }}
                     setRange={(range) => {
                        simpleResumeManager.pagination.setRange(range);
                     }}
                     range={simpleResumeManager.pagination.range}
                  />

                  <Flex align="center" gap="2">
                     <Flex align="center" gap="1">
                        <Text size="3" color="gray">
                           Total pages:
                        </Text>
                        <Text size="3" weight="medium">
                           {simpleResumeManager.totalPages === 0
                              ? ''
                              : simpleResumeManager.totalPages}
                        </Text>
                     </Flex>

                     <TextField.Root
                        className="simple-resume-manager__page-input"
                        value={simpleResumeManager.toPage}
                        disabled={simpleResumeManager.loading}
                        onChange={(e) => {
                           simpleResumeManager.onToPageChange(e.target.value);
                        }}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              simpleResumeManager.goToPage();
                           }
                        }}
                        size="3"
                        color="blue"
                        type="number"
                        min={1}
                        max={simpleResumeManager.totalPages}
                     />
                  </Flex>
               </Flex>
            </Box>
         </Box>

         <Dialog.Root
            open={open}
            onOpenChange={(nextOpen) => {
               if (!nextOpen) {
                  setAction('EMPTY');
               }
               setOpen(nextOpen);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(event) => event.preventDefault()}
            >
               {
                  {
                     ACTIVATE_ALL: (
                        <SimpleResumeConfirmDialog
                           title="Activate Resumes"
                           message="Do you want to Activate selected resumes?"
                           loading={simpleResumeManager.activateAllResumesState.status === 'pending'}
                           onConfirm={() => {
                              simpleResumeManager.activateAll({
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp.message);
                                 },
                                 onError: (message) => {
                                    toast.error(message);
                                 },
                              });
                           }}
                        />
                     ),
                     DEACTIVATE_ALL: (
                        <SimpleResumeConfirmDialog
                           title="Deactivate Resumes"
                           message="Do you want to Deactivate selected resumes?"
                           loading={
                              simpleResumeManager.deactivateAllResumesState.status === 'pending'
                           }
                           onConfirm={() => {
                              simpleResumeManager.deactivateAll({
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp.message);
                                 },
                                 onError: (message) => {
                                    toast.error(message);
                                 },
                              });
                           }}
                        />
                     ),
                     DELETE_ALL: (
                        <SimpleResumeConfirmDialog
                           title="Delete Resumes"
                           message="Do you want to Delete selected resumes?"
                           loading={simpleResumeManager.deleteAllResumesState.status === 'pending'}
                           onConfirm={() => {
                              simpleResumeManager.deleteAll({
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp.message);
                                 },
                                 onError: (message) => {
                                    toast.error(message);
                                 },
                              });
                           }}
                        />
                     ),
                     EMPTY: <></>,
                  }[action]
               }
            </Dialog.Content>
         </Dialog.Root>
      </>
   );
}

export const SimpleResumeManager = observer(SimpleResumeManagerView);
